import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { getLedgerDetails } from "../../../../utils/api";
import { Button, InputField, PageLoader, Table } from "../../../../v2/design-system";
import { theme } from "../../../../v2/design-system/theme";
import { HubFieldCell, HubFieldGrid, HubMutedText, HubNarrationCell } from "../societyHubShell";
import { HubViewRowButton } from "../tableShared";

const OrgWalletLedgerTable = ({ userId, data = [], loading: tableLoading = false, pagination }) => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleOpenModal = async (row) => {
    setLoading(true);
    try {
      const payload = {
        orgUserId: userId,
        pageNo: 0,
        pageSize: 10,
      };
      const response = await getLedgerDetails(row.voucherId, payload);
      const ledgerData = response?.data?.data || {};
      setSelectedRow(ledgerData);
      setOpenModal(true);
    } catch {
      setSelectedRow(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedRow(null);
  };

  const columns = [
      { key: "voucherId", label: "Transaction ID", align: "left" },
      {
        key: "txnAmount",
        label: "Transaction Amount",
        render: (_, row) => (row?.txnAmount ? `₹${row.txnAmount}` : "0"),
      },
      {
        key: "openingBalance",
        label: "Opening Balance",
        render: (_, row) => (row?.openingBalance ? `₹${row.openingBalance}` : "0"),
      },
      {
        key: "closingBalance",
        label: "Closing Balance",
        render: (_, row) => (row?.closingBalance ? `₹${row.closingBalance}` : "0"),
      },
      { key: "txnType", label: "Transaction Type", align: "left" },
      {
        key: "narration",
        label: "Narration",
        align: "left",
        render: (_, row) => <HubNarrationCell fullText={row?.narration} limit={12} />,
      },
      { key: "txnMode", label: "Transaction Mode", align: "left" },
      {
        key: "finalAmount",
        label: "Final Amount",
        render: (_, row) => (row?.finalAmount ? `₹${row.finalAmount}` : "0"),
      },
      {
        key: "gstCharges",
        label: "GST Charges",
        render: (_, row) => (row?.gstCharges ? `₹${row.gstCharges}` : "0"),
      },
      {
        key: "totalServiceFee",
        label: "Total Service Fee",
        render: (_, row) => (row?.totalServiceFee ? `₹${row.totalServiceFee}` : "0"),
      },
      { key: "txnStatus", label: "Status", align: "left" },
      {
        key: "actions",
        label: "Actions",
        render: (_, row) => (
          <HubViewRowButton ariaLabel="View ledger row details" onClick={() => handleOpenModal(row)} />
        ),
      },
    ];

  const formatLabel = (key) =>
    key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase());

  const formatValue = (key, value) => {
    if (value === null || value === undefined) return "";
    if (
      ["amount", "txnAmount", "finalAmount", "openingBalance", "closingBalance", "gstCharges", "totalServiceFee"].includes(
        key
      )
    ) {
      return `₹${value}`;
    }
    if (key.toLowerCase().includes("date")) {
      return new Date(value).toLocaleString();
    }
    return value;
  };

  return (
    <>
      <Table
        data={data}
        columns={columns}
        rowKey="transactionId"
        loading={tableLoading}
        emptyMessage="No ledger entries"
        pagination={pagination}
      />

      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>Ledger Details</DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <PageLoader />
          ) : selectedRow && Object.keys(selectedRow).length > 0 ? (
            <HubFieldGrid>
              {Object.keys(selectedRow).map((key) => (
                <HubFieldCell key={key} $span={6}>
                  <InputField
                    label={formatLabel(key)}
                    value={String(formatValue(key, selectedRow[key]))}
                    readOnly
                    disabled
                  />
                </HubFieldCell>
              ))}
            </HubFieldGrid>
          ) : (
            <HubMutedText>No data available</HubMutedText>
          )}
        </DialogContent>
        <DialogActions>
          <Button type="button" variant="secondary" size="sm" onClick={handleCloseModal}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrgWalletLedgerTable;
